import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Badge
} from 'antd';
import ReactMarkdown from 'react-markdown';
import {
  DatabaseOutlined,
  WifiOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';

// Import card components
import { ArtifactListCard } from '../Cards/ArtifactListCard';
import { CompositeCardRenderer } from '../CompositeCards/CompositeCardRenderer';
import { ArtifactContextCard } from '../CompositeCards/ContextCards';
import { DemoHelp } from '../Demo/DemoHelp';
import { ThreadAssignmentDialog } from '../Demo/ThreadAssignmentDialog';

// Import contexts
import { useModelSelector } from '../../contexts/ModelSelectorContext';
import { useDataConnection } from '../../contexts/DataConnectionContext';
import { useChat } from '../../contexts/ChatContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useUser } from '../../contexts/UserContext';
import { useCommands } from '../../contexts/CommandContext';
import { useChatUI } from '../../contexts/ChatUIContext';
import { ThreadAssignmentService } from '../../services/threadAssignmentService';

// Import components
import { HeaderUserMenu } from '../Header/HeaderUserMenu';
import { ValueGenerator } from '../Header/ValueGenerator';
import { ChatUIModelSelector } from '../ChatUI/ChatUIModelSelector';
import { ChatUIRoleSelector } from '../ChatUI/ChatUIRoleSelector';
import { HamburgerMenu } from '../Header/HamburgerMenu';
import { PinnedThreads } from '../PinnedThreads/PinnedThreads';
import { ChatInput } from '../ChatInput/ChatInput';
import { AgentReplyWaitIndicator } from '../ChatUI/AgentReplyWaitIndicator';
import { CommandHelp } from '../CommandHelp/CommandHelp';
import { TaskCard } from '../Cards';
import { FocusList } from '../FocusList';
import { RecentThreads } from '../RecentThreads';
import { ThreadContext } from '../ThreadContext';
import { SidebarSection } from '../common/SidebarSection';
import { SidebarToggle } from '../common/SidebarToggle';
import { NextActionPanel } from '../ExoPanel';

// Import types
import { OperationalMode } from '../../contexts/ModelSelectorContext';
import { ChatMessage } from '../../contexts/ChatContext';
import { chatVisibleModelRead } from './chatModelRead';
import { ThreadService } from '../../services/api/threadService';
import styles from './ChatOperationalLayout.module.css';

const { Text, Title } = Typography;

interface ChatOperationalLayoutProps {
  onToggleUI?: () => void;
  showUIToggle?: boolean;
  demoMode?: boolean;
  demoSession?: any; // Demo session information
  demoSceneData?: any; // Demo scene data for task information
  onMessage?: (message: string) => void;
  onCardAction?: (action: string, payload: any) => void;
  showHeader?: boolean;
  /** Hide Exo Panel "What Must Happen Next" (e.g. embedded Bridge Session domain builder). */
  hideNextActionPanel?: boolean;
}

export const ChatOperationalLayout: React.FC<ChatOperationalLayoutProps> = ({
  onToggleUI: _onToggleUI,
  showUIToggle: _showUIToggle = true,
  demoMode: propDemoMode = false,
  demoSession = null,
  demoSceneData = null,
  showHeader = false,
  hideNextActionPanel = false,
  onMessage,
  onCardAction,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  // Check for demo mode from location state
  const locationDemoState = location.state as { isDemo?: boolean; userId?: string; companyId?: string; artifactTreeId?: string } | null;
  const isDemoFromLocation = locationDemoState?.isDemo || false;
  const demoMode = propDemoMode || isDemoFromLocation;
  
  // Use context hooks
  const { 
    uiRole: modelSelectorRole, 
    environment: env, 
    dataSource, 
    switchToRole 
  } = useModelSelector();
  const { 
    artifacts, 
    isLoading, 
    syncData 
  } = useDataConnection();
  const { 
    messages, 
    setMessages,
    sendMessage, 
    createThread,
    selectThread: chatSelectThread,
    addMessage,
    currentThreadId,
    threads,
    isLoading: chatLoading,
    isLoadingMessages,
    isTyping,
    loadUserThreads,
    loadMessages: chatLoadMessages
  } = useChat();
  const { user, selectedCompany } = useUser();
  const { 
    selectedCommand, 
    showCommandHelp, 
    setShowCommandHelp,
    executeCommand,
    availableCommands
  } = useCommands();
  const { uiRole: chatUIRole } = useChatUI();
  
  // UX Principle: Layout role is determined by UI selector, not backend thread state
  // This ensures the Exo Panel layout remains stable when messages are sent
  // The layout role comes from ChatUIContext (user's explicit selection) with fallback to ModelSelectorContext
  const layoutRole: 'architect' | 'operator' | 'coach' | 'integrator' | 'development' | 'marketfit' = 
    chatUIRole || modelSelectorRole || 'operator'; // Default to operator (Execution Mode)
  
  // Single flag for Execution Mode (operator) to gate all operator-only UI
  const isExecutionMode = layoutRole === 'operator';
  
  // Get current thread for reference (but don't use its uiRole for layout decisions)
  const currentThread = threads.find(t => t.id === currentThreadId);
  const {
    selectedPinnedArtifactId,
    selectedThreadId,
    selectedFocusTaskId,
    rightPanelContent,
    newThreadContext,
    selectCenterCard,
    clearNewThreadContext,
  } = useNavigation();

  // Local state
  const [selectedArtifactId, setSelectedArtifactId] = useState<string>('');
  const [selectedArtifactForContext, setSelectedArtifactForContext] = useState<any | null>(null);
  const [input, setInput] = useState('');
  const [isLoadingLiveData, setIsLoadingLiveData] = useState(false);
  const [liveDataError, setLiveDataError] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [demoHelpExpanded, setDemoHelpExpanded] = useState(false);
  const [selectedSceneForHelp, setSelectedSceneForHelp] = useState<number | null>(null);
  const [threadAssignmentDialog, setThreadAssignmentDialog] = useState<{
    visible: boolean;
    threadData: any | null;
  }>({ visible: false, threadData: null });
  // UX Principle #10: Present-focused by default - hide history in Execution Mode
  const [showHistory, setShowHistory] = useState(false);
  // UX Principle #6: Context is pull-based - minimize context by default
  const [showContextPanel, setShowContextPanel] = useState(false);
  const centerRef = useRef<HTMLDivElement>(null);
  const processedContextsRef = useRef<Set<string>>(new Set());

  // Handle card actions (local + prop)
  const handleLocalCardAction = useCallback(async (action: string, payload: any) => {
    console.log('🎯 [ChatOperationalLayout] Card action:', { action, payload });
    
    // Handle show-context action
    if (action === 'show-context' && payload?.type === 'artifact' && payload?.artifact) {
      // Enhance artifact with thread's artifact_tree_id if missing
      const artifact = { ...payload.artifact };
      
      // If artifact doesn't have artifact_tree_id, try to get it from the current thread
      if (!artifact.artifact_tree_id && currentThread?.model) {
        const threadArtifactTreeId = currentThread.model.artifactTreeId;
        if (threadArtifactTreeId) {
          artifact.artifact_tree_id = threadArtifactTreeId;
          if (process.env.NODE_ENV === 'development') {
            console.log('🔧 [ChatOperationalLayout] Enhanced artifact with thread artifact_tree_id:', {
              artifactId: artifact.id,
              artifactName: artifact.name,
              artifactTreeId: threadArtifactTreeId
            });
          }
        }
      }
      
      // Also try to get company_id from thread context if available
      if (!artifact.company_id && currentThread?.context?.companyId) {
        artifact.company_id = currentThread.context.companyId;
      }
      
      setSelectedArtifactForContext(artifact);
    }
    
    // Handle execute-transition action
    if (action === 'execute-transition' && payload?.itemId && payload?.transitionId && payload?.artifactId) {
      try {
        const { StateMachineService } = await import('../../services/api/stateMachineService');
        await StateMachineService.executeTransition(
          payload.artifactId,
          payload.itemId,
          payload.transitionId,
          {
            transition_data: payload.transition_data || {},
            reason: payload.reason || 'Executed from chat UI',
            transitioned_by: user?.id
          }
        );
        
        // Add success message to chat
        addMessage({
          threadId: currentThreadId || '',
          who: 'assistant',
          text: `Transition executed successfully on item ${payload.item?.name || payload.itemId}`,
          type: 'text',
          metadata: {}
        });
        
        // Refresh the card or show updated state
        if (onCardAction) {
          onCardAction('refresh-item', { itemId: payload.itemId });
        }
      } catch (error: any) {
        console.error('Error executing transition:', error);
        addMessage({
          threadId: currentThreadId || '',
          who: 'assistant',
          text: `Failed to execute transition: ${error.response?.data?.detail || error.message || 'Unknown error'}`,
          type: 'error',
          metadata: { error: error.response?.data?.detail || error.message || 'Unknown error' }
        });
      }
    }
    
    // Handle update-property action
    if (action === 'update-property' && payload?.itemId) {
      // Trigger property update UI (could open a modal or navigate)
      if (onCardAction) {
        onCardAction('open-property-editor', payload);
      }
    }
    
    // Handle view-history action
    if (action === 'view-history' && payload?.itemId) {
      // Show state history (could open a modal or navigate)
      if (onCardAction) {
        onCardAction('show-state-history', payload);
      }
    }
    
    // Also call the prop callback if provided (for demo mode)
    onCardAction?.(action, payload);
  }, [onCardAction, currentThread, currentThreadId, user, addMessage]);

  // Load artifacts when data source changes
  const loadArtifacts = useCallback(async () => {
    if (dataSource === 'live') {
      setIsLoadingLiveData(true);
      setLiveDataError(null);
      try {
        await syncData(true);
      } catch (error: any) {
        setLiveDataError(error.message || 'Failed to load live data');
        console.error('Failed to load live data:', error);
      } finally {
        setIsLoadingLiveData(false);
      }
    }
  }, [dataSource, syncData, artifacts.length]);

  useEffect(() => {
    void loadArtifacts();
  }, [loadArtifacts]);

  // Debug: Log available commands (only when commands are actually available)
  useEffect(() => {
    if (availableCommands.length > 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Available commands:', availableCommands.map(cmd => cmd.name));
      }
    }
  }, [availableCommands]);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (centerRef.current) {
      centerRef.current.scrollTop = centerRef.current.scrollHeight;
    }
  }, [messages]);

  // Handle demo mode - create demo thread if needed
  useEffect(() => {
    if (demoMode && isDemoFromLocation && locationDemoState && !currentThreadId && user) {
      // Create a demo thread with demo context
      console.log('🔍 [ChatOperationalLayout] Creating demo thread with context:', locationDemoState);
      createThread(
        'Demo Chat',
        'Demonstration of Krysalis platform',
        {
          type: 'company' as any,
          artifactTreeId: locationDemoState.artifactTreeId || undefined
        },
        'architect',
        {
          isDemo: true,
          userId: locationDemoState.userId || undefined,
          companyId: locationDemoState.companyId || undefined,
          artifactTreeId: locationDemoState.artifactTreeId || undefined
        } as any
      ).then((thread) => {
        console.log('🔍 [ChatOperationalLayout] Created demo thread:', thread.id);
        chatSelectThread(thread.id);
      }).catch((error) => {
        console.error('Failed to create demo thread:', error);
      });
    }
  }, [demoMode, isDemoFromLocation, locationDemoState, currentThreadId, user, createThread, chatSelectThread]);
  
  // Handle demo mode - select demo session's thread (let ChatContext load messages)
  useEffect(() => {
    if (demoMode && demoSession?.session_data?.thread_id) {
      const demoThreadId = demoSession.session_data.thread_id;
      console.log('🔍 [ChatOperationalLayout] Demo mode: checking if demo thread should be selected:', demoThreadId);
      
      // Only select if not already selected
      // ChatContext will handle loading messages for this thread
      if (currentThreadId !== demoThreadId) {
        console.log('🔍 [ChatOperationalLayout] Selecting demo thread (ChatContext will load messages):', demoThreadId);
        chatSelectThread(demoThreadId);
      }
    }
  }, [demoMode, demoSession?.session_data?.thread_id, currentThreadId, chatSelectThread]);

  // Refresh messages periodically when in demo mode (using ChatContext's reload mechanism)
  useEffect(() => {
    if (!demoMode || !currentThreadId) return;
    
    let refreshCount = 0;
    const maxRefreshes = 10; // Limit to prevent excessive API calls
    
    const refreshInterval = setInterval(async () => {
      try {
        refreshCount++;
        
        // Stop refreshing after max attempts to prevent infinite loops
        if (refreshCount > maxRefreshes) {
          console.log('🔍 [ChatOperationalLayout] Stopping demo message refresh after', maxRefreshes, 'attempts');
          clearInterval(refreshInterval);
          return;
        }
        
        console.log('🔍 [ChatOperationalLayout] Refreshing thread messages... (attempt', refreshCount, '/', maxRefreshes, ')');
        
        // Use ChatContext's loadMessages with clearExisting=false to only add new messages
        // This prevents re-rendering and disrupting user interactions with cards
        chatLoadMessages(currentThreadId, false);
        
      } catch (error) {
        console.error('Failed to refresh thread messages:', error);
      }
    }, 5000); // Refresh every 5 seconds
    
    return () => clearInterval(refreshInterval);
  }, [demoMode, currentThreadId, chatLoadMessages]);

  // Initialize selected scene for help when demo session loads
  useEffect(() => {
    if (demoMode && demoSession && selectedSceneForHelp === null) {
      setSelectedSceneForHelp(demoSession.current_scene);
    }
  }, [demoMode, demoSession, selectedSceneForHelp]);

  // Handle creating new threads when items are selected (with loop prevention)
  useEffect(() => {
    const createNewThreadForSelection = async () => {
      if (newThreadContext.type && newThreadContext.itemId) {
        const contextKey = `${newThreadContext.type}-${newThreadContext.itemId}`;
        
        console.log('Processing thread context:', { type: newThreadContext.type, itemId: newThreadContext.itemId, contextKey });
        
        // Prevent infinite loops by checking if we're currently processing this exact context
        // But allow different contexts to be processed
        if (processedContextsRef.current.has(contextKey)) {
          console.log('Context already being processed, skipping to prevent infinite loop:', contextKey);
          return;
        }
        
        try {
          // Mark this context as being processed
          processedContextsRef.current.add(contextKey);
          console.log('Marked context as being processed:', contextKey);
          
          let threadName = '';
          let threadDescription = '';

          if (newThreadContext.type === 'pinned-artifact') {
            // Handle pinned artifact ID (might have "pinned-" prefix)
            let artifactId = newThreadContext.itemId;
            if (artifactId.startsWith('pinned-')) {
              artifactId = artifactId.replace('pinned-', '');
            }
            const artifact = artifacts.find(a => a.id === artifactId);
            threadName = `Artifact Discussion: ${artifact?.name || 'Unknown'}`;
            threadDescription = `Discussion about the ${artifact?.type || 'artifact'} "${artifact?.name || 'Unknown'}"`;
          } else if (newThreadContext.type === 'focus-task') {
            // For focus tasks, we'd need to get task details from the sample service
            threadName = `Task Focus: ${newThreadContext.itemId}`;
            threadDescription = `Working on focus task: ${newThreadContext.itemId}`;
          }

          if (threadName) {
            console.log('Creating thread:', threadName);
            const newThread = await createThread(threadName, threadDescription);
            console.log('Created new thread for selection:', newThread);
            
            // Switch to the new thread
            console.log('Switching to thread:', newThread.id);
            await chatSelectThread(newThread.id);
            
            // Add initial message with card for pinned artifacts or focus tasks
            if (newThreadContext.type === 'pinned-artifact') {
              let artifactId = newThreadContext.itemId;
              if (artifactId.startsWith('pinned-')) {
                artifactId = artifactId.replace('pinned-', '');
              }
              const artifact = artifacts.find(a => a.id === artifactId);
              
              if (artifact) {
                console.log('Adding welcome message for artifact:', artifact.name);
                // Add welcome message
                addMessage({
                  threadId: newThread.id,
                  who: 'assistant',
                  text: `Let's discuss the ${artifact.type} "${artifact.name}". What would you like to know about it?`,
                  type: 'text',
                });
                
                console.log('Adding artifact card message');
                // Add artifact card message
                addMessage({
                  threadId: newThread.id,
                  who: 'assistant',
                  text: `Here are the details for "${artifact.name}":`,
                  type: 'card',
                  metadata: {
                    cardType: 'artifact-detail',
                    artifactId: artifact.id,
                  },
                });
              }
            } else if (newThreadContext.type === 'focus-task') {
              console.log('Adding welcome message for focus task:', newThreadContext.itemId);
              
              // Get task title for the welcome message
              const getTaskTitle = (taskId: string) => {
                switch (taskId) {
                  case 'task-1': return 'Review Financial Reconciliation Process';
                  case 'task-2': return 'Optimize Customer Onboarding Workflow';
                  case 'task-3': return 'Implement Automated Reporting System';
                  default: return 'Focus Task';
                }
              };
              
              const taskTitle = getTaskTitle(newThreadContext.itemId);
              
              // Add welcome message for focus task
              addMessage({
                threadId: newThread.id,
                who: 'assistant',
                text: `Let's work on the focus task "${taskTitle}". What would you like to accomplish?`,
                type: 'text',
              });
              
              console.log('Adding task card message');
              // Add task card message
              addMessage({
                threadId: newThread.id,
                who: 'assistant',
                text: `Here are the task details:`,
                type: 'card',
                metadata: {
                  cardType: 'task-detail',
                  taskId: newThreadContext.itemId,
                } as any,
              });
            }
            
            // Clear the new thread context after successful processing
            clearNewThreadContext();
          }
          
          // Remove from processed set after successful completion so it can be processed again if needed
          processedContextsRef.current.delete(contextKey);
          console.log('Removed context from processed set:', contextKey);
        } catch (error) {
          console.error('Failed to create thread for selection:', error);
          // Remove from processed set on error so it can be retried
          processedContextsRef.current.delete(contextKey);
          // Clear the context even on error to prevent infinite loops
          clearNewThreadContext();
        }
      }
    };

    void createNewThreadForSelection();
  }, [newThreadContext.type, newThreadContext.itemId, artifacts, createThread, chatSelectThread, addMessage, clearNewThreadContext]);

  // Thread assignment handlers
  const handleThreadAssignment = useCallback(async (threadId: string, userId: string) => {
    try {
      const result = await ThreadAssignmentService.assignThreadToUser(threadId, userId);
      if (result.success) {
        // Close the dialog
        setThreadAssignmentDialog({ visible: false, threadData: null });
        // Reload user threads to show the newly assigned thread
        await loadUserThreads();
        // Select the assigned thread
        await chatSelectThread(threadId);
      }
    } catch (error) {
      console.error('Failed to assign thread:', error);
      throw error;
    }
  }, [loadUserThreads, chatSelectThread]);

  const handleThreadClick = useCallback(async (threadId: string) => {
    if (demoMode && demoSession) {
      // In demo mode, show assignment dialog for demo threads
      try {
        const threadData = await ThreadAssignmentService.getThreadDetails(threadId);
        setThreadAssignmentDialog({
          visible: true,
          threadData: threadData
        });
      } catch (error) {
        console.error('Failed to get thread details:', error);
        // Fall back to normal thread selection
        await chatSelectThread(threadId);
      }
    } else {
      // Normal thread selection
      await chatSelectThread(threadId);
    }
  }, [demoMode, demoSession, chatSelectThread]);

  const handleThreadAssignmentCancel = useCallback(() => {
    setThreadAssignmentDialog({ visible: false, threadData: null });
  }, []);

  // Handle data source change
  // const handleDataSourceChange = (newDataSource: DataSource) => {
  //   setDataSource(newDataSource);
  // };

  // Note: UI role and model for AppShell routes come from ChatUIContext + ModelSelectorContext.
  // Legacy standalone header mode still exposes ChatUIModelSelector / ChatUIRoleSelector in the sidebar.

  // Handle user change
  const handleUserChange = (user: any) => {
    // Only log when user actually changes to a non-null value
    if (user && process.env.NODE_ENV === 'development') {
      console.log('User changed:', user);
    }
  };

  // Handle message send
  const handleSendMessage = async (message: string) => {
    if (!message.trim() || !currentThreadId) return;
    
    console.log('💬 [ChatOperationalLayout] Handling message:', message);
    
    try {
      // Add user's message to chat first
      addMessage({
        threadId: currentThreadId,
        who: 'user',
        text: message.trim(),
        type: 'text',
      });
      
      // If it's NOT a command, send to backend (for AI processing later)
      if (!message.trim().startsWith('/')) {
        await sendMessage(message.trim());
      }
    } catch (error) {
      console.error('❌ [ChatOperationalLayout] Failed to send message:', error);
    }
  };

  // Handle command execution
  const handleCommand = async (command: string, args: string[] = []) => {
    if (!currentThreadId) {
      console.error('❌ [ChatOperationalLayout] No current thread available for command execution');
      return;
    }
    
    if (!user?.id) {
      console.error('❌ [ChatOperationalLayout] Cannot execute command: user is not available');
      return;
    }
    
    try {
      console.log('⚡ [ChatOperationalLayout] Executing command:', command, 'with args:', args);
      
      // Save the user's command message to backend first
      const commandText = `/${command}${args.length > 0 ? ' ' + args.join(' ') : ''}`;
      try {
        console.log('💾 [ChatOperationalLayout] Saving user command message to backend:', commandText);
        const savedUserMessage = await ThreadService.sendMessage(currentThreadId, {
          user_id: user.id,
          content: commandText,
          type: 'text',
        });
        console.log('✅ [ChatOperationalLayout] User command message saved:', savedUserMessage);
        // Update the local message with the backend ID if it exists
        setMessages(prev => prev.map(m => 
          (m.threadId === currentThreadId && m.who === 'user' && m.text === commandText && !m.id.startsWith('temp-'))
            ? { ...m, id: savedUserMessage.id, timestamp: savedUserMessage.timestamp }
            : m
        ));
      } catch (error) {
        console.error('❌ [ChatOperationalLayout] Failed to save user command message:', error);
        // Continue with command execution even if saving fails
      }
      
      // Execute the command using the command system
      const result = await executeCommand(commandText);
      
      console.log('✅ [ChatOperationalLayout] Command result:', { 
        success: result.success, 
        hasCards: !!result.cards,
        cardCount: result.cards?.length,
        resultData: result.data,
        resultDataType: result.data?.type,
        resultDataKeys: result.data ? Object.keys(result.data) : []
      });
      
      if (result.success) {
        // Determine message type and cards based on command result
        let messageType = 'text';
        let cards = null;
        
        // Check for artifact_list in result.data - multiple possible structures
        // Structure 1: { type: "artifact_list", artifacts: [...] }
        if (result.data && result.data.type === 'artifact_list' && result.data.artifacts && Array.isArray(result.data.artifacts)) {
          messageType = 'artifact_list';
          cards = result.data.artifacts;
          console.log('📋 [ChatOperationalLayout] Detected artifact_list (structure 1) with', cards.length, 'artifacts');
        } 
        // Structure 2: { data: { artifacts: [...] } } - nested structure
        else if (result.data && result.data.data && result.data.data.artifacts && Array.isArray(result.data.data.artifacts)) {
          messageType = 'artifact_list';
          cards = result.data.data.artifacts;
          console.log('📋 [ChatOperationalLayout] Detected artifact_list (structure 2) with', cards.length, 'artifacts');
        }
        // Structure 3: artifacts array directly in result.data (fallback)
        else if (result.data && Array.isArray(result.data.artifacts) && result.data.artifacts.length > 0) {
          messageType = 'artifact_list';
          cards = result.data.artifacts;
          console.log('📋 [ChatOperationalLayout] Detected artifact_list (structure 3) with', cards.length, 'artifacts');
        }
        // Check for thread_list - similar pattern matching
        else if (result.data && result.data.type === 'thread_list' && result.data.threads && Array.isArray(result.data.threads)) {
          messageType = 'thread_list';
          cards = result.data.threads;
          console.log('📋 [ChatOperationalLayout] Detected thread_list (structure 1) with', cards.length, 'threads');
        }
        // Thread list nested structure
        else if (result.data && result.data.data && result.data.data.threads && Array.isArray(result.data.data.threads)) {
          messageType = 'thread_list';
          cards = result.data.data.threads;
          console.log('📋 [ChatOperationalLayout] Detected thread_list (structure 2) with', cards.length, 'threads');
        }
        // Thread list fallback
        else if (result.data && Array.isArray(result.data.threads) && result.data.threads.length > 0) {
          messageType = 'thread_list';
          cards = result.data.threads;
          console.log('📋 [ChatOperationalLayout] Detected thread_list (structure 3) with', cards.length, 'threads');
        }
        
        // Add the command result as an assistant message and save to backend
        const assistantMessage = addMessage({
          threadId: currentThreadId,
          who: 'assistant',
          text: result.message,
          type: messageType as any,
          cards: cards,
          metadata: {
            commandHistory: {
              command: command,
              args: args,
              executionTime: result.metadata?.executionTime,
              dataSource: result.metadata?.dataSource,
            },
            commandResult: result.data
          } as any
        });
        
        // Save to backend if in live mode
        if (dataSource === 'live') {
          try {
            console.log('🔍 [ChatOperationalLayout] Saving command result message to backend');
            
            // Determine message type and cards based on command result (duplicate logic for backend save)
            let messageType = 'text';
            let cards = null;
            
            // Use the same pattern matching logic as above
            if (result.data && result.data.type === 'artifact_list' && result.data.artifacts && Array.isArray(result.data.artifacts)) {
              messageType = 'artifact_list';
              cards = result.data.artifacts;
            } else if (result.data && result.data.data && result.data.data.artifacts && Array.isArray(result.data.data.artifacts)) {
              messageType = 'artifact_list';
              cards = result.data.data.artifacts;
            } else if (result.data && Array.isArray(result.data.artifacts) && result.data.artifacts.length > 0) {
              messageType = 'artifact_list';
              cards = result.data.artifacts;
            } else if (result.data && result.data.type === 'thread_list' && result.data.threads && Array.isArray(result.data.threads)) {
              messageType = 'thread_list';
              cards = result.data.threads;
            } else if (result.data && result.data.data && result.data.data.threads && Array.isArray(result.data.data.threads)) {
              messageType = 'thread_list';
              cards = result.data.data.threads;
            } else if (result.data && Array.isArray(result.data.threads) && result.data.threads.length > 0) {
              messageType = 'thread_list';
              cards = result.data.threads;
            }
            
            // Ensure user_id is provided for the message
            if (!user?.id) {
              console.error('❌ [ChatOperationalLayout] Cannot save command result message: user is not available');
              return;
            }
            
            const savedMessage = await ThreadService.sendMessage(currentThreadId, {
              user_id: user.id, // CRITICAL: user_id is required by the backend
              threadId: currentThreadId,
              who: 'assistant',
              text: result.message,
              type: messageType as any,
              cards: cards,
              metadata: {
                commandHistory: {
                  command: command,
                  args: args,
                  executionTime: result.metadata?.executionTime,
                  dataSource: result.metadata?.dataSource,
                },
                commandResult: result.data
              } as any
            });
            console.log('✅ [ChatOperationalLayout] Command result message saved:', savedMessage);
            // Update the local message with the backend ID
            setMessages(prev => prev.map(m => 
              m.id === assistantMessage.id ? { ...m, id: savedMessage.id } : m
            ));
          } catch (error) {
            console.error('❌ [ChatOperationalLayout] Failed to save command result message:', error);
          }
        }
        
        // If the result has cards from result.cards OR cards extracted from result.data, add them as card messages
        // Convert cards extracted from result.data to card format if needed
        let cardsToSave: any[] = [];
        
        if (result.cards && result.cards.length > 0) {
          // Use cards from result.cards directly (already in card format)
          cardsToSave = result.cards;
        } else if (cards && cards.length > 0) {
          // Convert artifacts/threads from result.data to card format
          if (messageType === 'artifact_list' || messageType === 'thread_list') {
            cardsToSave = cards.map((item: any) => ({
              type: messageType === 'artifact_list' ? 'artifact' : 'thread',
              artifactId: item.id || item.artifact_id,
              artifact: item, // Include full artifact/thread data
              ...item, // Spread other properties
            }));
          } else {
            cardsToSave = cards;
          }
        }
        
        if (cardsToSave.length > 0) {
          console.log('📇 [ChatOperationalLayout] Adding', cardsToSave.length, 'card(s) to chat');
          
          for (const card of cardsToSave) {
            // Determine cardType from card structure
            const cardType = card.type || card.cardType || (card.artifactId ? 'artifact' : 'list');
            const artifactId = card.artifactId || card.id || card.artifact_id;
            
            const cardMessage = addMessage({
              threadId: currentThreadId,
              who: 'assistant',
              text: '', // Empty text - the card renders instead
              type: 'card',
              metadata: {
                cardType: cardType,
                artifactId: artifactId,
                taskId: card.taskId,
                artifact: card.artifact || card, // Include full artifact data for rendering
                commandHistory: {
                  command: command,
                  args: args,
                  executionTime: result.metadata?.executionTime,
                  dataSource: result.metadata?.dataSource,
                },
                ...card,
              },
            });
            
            // Save card message to backend if in live mode
            if (dataSource === 'live') {
              try {
                console.log('🔍 [ChatOperationalLayout] Saving card message to backend');
                
                // Ensure user_id is available
                if (!user?.id) {
                  console.error('❌ [ChatOperationalLayout] Cannot save card message: user is not available');
                  return;
                }
                
                // Determine cardType and artifactId for backend save
                const cardType = card.type || card.cardType || (card.artifactId ? 'artifact' : 'list');
                const artifactId = card.artifactId || card.id || card.artifact_id;
                
                // Create descriptive content for card messages (backend requires non-empty content)
                const cardContent = card.artifact?.name || card.name || 
                                  (cardType === 'artifact' ? `Artifact card: ${artifactId?.substring(0, 8) || 'unknown'}` :
                                   cardType === 'list' ? 'Artifact list card' :
                                   `Card: ${cardType}`);
                
                const savedCardMessage = await ThreadService.sendMessage(currentThreadId, {
                  user_id: user.id, // CRITICAL: user_id is required by the backend
                  threadId: currentThreadId,
                  who: 'assistant',
                  content: cardContent, // Use descriptive content instead of empty string
                  type: 'card',
                  metadata: {
                    cardType: cardType,
                    artifactId: artifactId,
                    taskId: card.taskId,
                    artifact: card.artifact || card, // Include full artifact data for rendering
                    commandHistory: {
                      command: command,
                      args: args,
                      executionTime: result.metadata?.executionTime,
                      dataSource: result.metadata?.dataSource,
                    },
                    ...card,
                  }
                });
                console.log('✅ [ChatOperationalLayout] Card message saved:', savedCardMessage);
                // Update the local message with the backend ID
                setMessages(prev => prev.map(m => 
                  m.id === cardMessage.id ? { ...m, id: savedCardMessage.id } : m
                ));
              } catch (error) {
                console.error('❌ [ChatOperationalLayout] Failed to save card message:', error);
              }
            }
          }
        }
        
        // If there are suggestions, we could show them in the UI
        if (result.suggestions && result.suggestions.length > 0) {
          console.log('💡 [ChatOperationalLayout] Command suggestions:', result.suggestions);
        }
      } else {
        // Show error message as assistant message
        console.warn('⚠️ [ChatOperationalLayout] Command failed:', result.error || result.message);
        const errorMessage = addMessage({
          threadId: currentThreadId,
          who: 'assistant',
          text: `❌ Command failed: ${result.error || result.message}`,
          type: 'error',
          metadata: {
            commandHistory: {
              command: command,
              args: args,
              executionTime: result.metadata?.executionTime,
              dataSource: result.metadata?.dataSource,
              success: false,
            }
          }
        });
        
        // Save error message to backend if in live mode
        if (dataSource === 'live') {
          try {
            console.log('🔍 [ChatOperationalLayout] Saving command error message to backend');
            const savedErrorMessage = await ThreadService.sendMessage(currentThreadId, {
              threadId: currentThreadId,
              who: 'assistant',
              text: `❌ Command failed: ${result.error || result.message}`,
              type: 'error',
              metadata: {
                commandHistory: {
                  command: command,
                  args: args,
                  executionTime: result.metadata?.executionTime,
                  dataSource: result.metadata?.dataSource,
                  success: false,
                }
              }
            });
            console.log('✅ [ChatOperationalLayout] Command error message saved:', savedErrorMessage);
            // Update the local message with the backend ID
            setMessages(prev => prev.map(m => 
              m.id === errorMessage.id ? { ...m, id: savedErrorMessage.id } : m
            ));
          } catch (error) {
            console.error('❌ [ChatOperationalLayout] Failed to save command error message:', error);
          }
        }
      }
    } catch (error) {
      console.error('❌ [ChatOperationalLayout] Failed to execute command:', error);
      const errorMessage = addMessage({
        threadId: currentThreadId,
        who: 'assistant',
        text: `❌ Command execution error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error',
        metadata: {
          commandHistory: {
            command: command,
            args: args,
            success: false,
            error: error instanceof Error ? error.message : String(error),
          }
        }
      });
      
      // Save error message to backend if in live mode
      if (dataSource === 'live') {
        try {
          console.log('🔍 [ChatOperationalLayout] Saving command execution error to backend');
          const savedErrorMessage = await ThreadService.sendMessage(currentThreadId, {
            threadId: currentThreadId,
            who: 'assistant',
            text: `❌ Command execution error: ${error instanceof Error ? error.message : 'Unknown error'}`,
            type: 'error',
            metadata: {
              commandHistory: {
                command: command,
                args: args,
                success: false,
                error: error instanceof Error ? error.message : String(error),
              }
            }
          });
          console.log('✅ [ChatOperationalLayout] Command execution error saved:', savedErrorMessage);
          // Update the local message with the backend ID
          setMessages(prev => prev.map(m => 
            m.id === errorMessage.id ? { ...m, id: savedErrorMessage.id } : m
          ));
        } catch (saveError) {
          console.error('❌ [ChatOperationalLayout] Failed to save command execution error:', saveError);
        }
      }
    }
  };


  // Get connection status
  const getConnectionStatus = () => {
    if (dataSource === 'sample') return { status: 'sample', color: '#6b7280', icon: <DatabaseOutlined /> };
    if (isLoadingLiveData) return { status: 'connecting', color: '#f59e0b', icon: <LoadingOutlined /> };
    if (liveDataError) return { status: 'error', color: '#ef4444', icon: <ExclamationCircleOutlined /> };
    if (isLoading) return { status: 'connected', color: '#10b981', icon: <WifiOutlined /> };
    return { status: 'connected', color: '#10b981', icon: <WifiOutlined /> };
  };

  const connectionStatus = getConnectionStatus();

  // Render card from message metadata
  const renderCardFromMessage = (message: ChatMessage) => {
    if (!message.metadata) {
      return null;
    }

    const { cardType, artifactId } = message.metadata;

    switch (cardType) {
      case 'artifact-overview':
        // Render all artifacts using Composite Card System
        return (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '12px', 
            width: '100%',
            maxWidth: '800px'
          }}>
            {artifacts.map((artifact) => (
              <div
                key={artifact.id}
                onClick={() => selectCenterCard(artifact.id, 'artifact')}
                style={{ cursor: 'pointer' }}
              >
                <CompositeCardRenderer
                  message={{
                    id: artifact.id,
                    threadId: currentThreadId || 'default-thread',
                    who: 'assistant',
                    text: `Artifact: ${artifact.name}`,
                    timestamp: new Date().toISOString(),
                    type: 'card',
                    metadata: {
                      cardType: 'artifact',
                      artifactId: artifact.id,
                      artifact: artifact
                    }
                  }}
                  context={{
                    threadId: currentThreadId || (demoMode ? 'demo-thread' : 'default-thread'),
                    userId: user?.id || 'current-user',
                    uiRole: layoutRole,
                    modelType: env || 'Company',
                    dataSource: dataSource
                  }}
                  onCardSelect={(card) => {
                    console.log('🎯 [ChatOperationalLayout] Artifact card selected:', card);
                    selectCenterCard(artifact.id, 'artifact');
                  }}
                  onCardAction={handleLocalCardAction}
                />
              </div>
            ))}
          </div>
        );

      case 'artifact-detail': {
        // Render specific artifact using Composite Card System
        const artifact = artifacts.find(a => a.id === artifactId);
        if (!artifact) return null;
        
        return (
          <div 
            style={{ maxWidth: '600px', cursor: 'pointer' }}
            onClick={() => selectCenterCard(artifact.id, 'artifact')}
          >
            <CompositeCardRenderer
              message={{
                id: artifact.id,
                threadId: currentThreadId || 'default-thread',
                who: 'assistant',
                text: `Artifact Details: ${artifact.name}`,
                timestamp: new Date().toISOString(),
                type: 'card',
                metadata: {
                  cardType: 'artifact',
                  artifactId: artifact.id,
                  artifact: artifact
                }
              }}
              context={{
                uiRole: layoutRole,
                modelType: env || 'Company',
                threadId: currentThreadId || (demoMode ? 'demo-thread' : 'default-thread'),
                userId: user?.id || 'current-user',
                dataSource: dataSource
              }}
              onCardSelect={(card) => {
                console.log('🎯 [ChatOperationalLayout] Artifact detail card selected:', card);
                selectCenterCard(artifact.id, 'artifact');
              }}
              onCardAction={handleLocalCardAction}
            />
          </div>
        );
      }

      case 'task-detail': {
        // Render task card
        const { taskId } = message.metadata as any;
        
        // Create different mock tasks based on the task ID
        const getMockTask = (taskId: string) => {
          switch (taskId) {
            case 'task-1':
              return {
                id: taskId,
                title: 'Review Financial Reconciliation Process',
                description: 'Monthly reconciliation process needs review and optimization. Current process takes too long.',
                type: 'process' as const,
                priority: 'high' as const,
                status: 'in_progress' as const,
                dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                assigneeId: 'u1',
                assigneeName: 'Finance Manager',
                artifactId: 'a4',
                artifactName: 'Treasury: Reconcile Accounts',
                progress: 65,
                tags: ['finance', 'reconciliation', 'optimization']
              };
            case 'task-2':
              return {
                id: taskId,
                title: 'Optimize Customer Onboarding Workflow',
                description: 'Streamline the customer onboarding process to reduce time-to-value and improve satisfaction scores.',
                type: 'improvement' as const,
                priority: 'critical' as const,
                status: 'pending' as const,
                dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
                assigneeId: 'u2',
                assigneeName: 'Customer Success Manager',
                artifactId: 'a1',
                artifactName: 'Customer Onboarding Process',
                progress: 25,
                tags: ['customer', 'onboarding', 'optimization', 'workflow']
              };
            case 'task-3':
              return {
                id: taskId,
                title: 'Implement Automated Reporting System',
                description: 'Develop and deploy automated reporting system for monthly financial statements and KPIs.',
                type: 'artifact' as const,
                priority: 'medium' as const,
                status: 'in_progress' as const,
                dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
                assigneeId: 'u3',
                assigneeName: 'IT Developer',
                artifactId: 'a2',
                artifactName: 'Financial Reporting System',
                progress: 80,
                tags: ['automation', 'reporting', 'finance', 'development']
              };
            default:
              return {
                id: taskId || 'task-1',
                title: 'General Task',
                description: 'A general focus task for demonstration purposes.',
                type: 'review' as const,
                priority: 'low' as const,
                status: 'pending' as const,
                dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                assigneeId: 'u4',
                assigneeName: 'Project Manager',
                progress: 10,
                tags: ['general', 'review']
              };
          }
        };
        
        const mockTask = getMockTask(taskId || 'task-1');
        
        return (
          <div style={{ maxWidth: '600px' }}>
            <TaskCard
              task={mockTask}
              onSelect={(taskId) => {
                console.log('Task selected from message:', taskId);
                selectCenterCard(taskId, 'task');
              }}
            />
          </div>
        );
      }

      default:
        return null;
    }
  };

  const sidebarWidth = sidebarCollapsed ? '60px' : '280px';
  const gridTemplateAreas = showHeader
    ? '"header header header" "sidebar center right-panel"'
    : '"sidebar center right-panel"';

  return (
    <div
      className={`chat-operational-layout ${styles.root} ${showHeader ? styles.standalone : `${styles.embedded} chat-operational-layout--embedded`}`}
      style={{
        gridTemplateColumns: `${sidebarWidth} 1fr 300px`,
        gridTemplateRows: showHeader ? '60px 1fr' : '1fr',
        gridTemplateAreas,
      }}
      data-testid="chat-operational-layout"
      data-embedded={showHeader ? 'false' : 'true'}
    >
      {/* Legacy standalone header — retired for /chat-operational (AppShell provides chrome). */}
      {showHeader && (
        <header className={styles.legacyHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <HamburgerMenu 
              onNavigateBack={() => navigate('/')}
              onDataSourceChange={(source) => {
                console.log('Data source changed to:', source);
              }}
            />
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <img 
                src="/krysalis-logo.svg" 
                alt="Krysalis Logo" 
                style={{ 
                  height: '32px', 
                  width: 'auto',
                  marginRight: '8px'
                }} 
              />
              <Title level={4} style={{ margin: 0, color: '#1f2937' }}>
                {selectedCompany?.name || 'Krysalis Business'}
              </Title>
              {process.env.NODE_ENV === 'development' && (
                <Badge 
                  color={connectionStatus.color} 
                  text={
                    <Text style={{ fontSize: '12px', color: connectionStatus.color }}>
                      {connectionStatus.status}
                    </Text>
                  }
                />
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ValueGenerator compact={true} />
            <HeaderUserMenu onUserChange={handleUserChange} />
          </div>
        </header>
      )}

      {/* Left Rail */}
      <aside
        className={styles.sidebar}
        style={{
          width: sidebarWidth,
        }}
      >
        {/* Sidebar Toggle Button */}
        <SidebarToggle 
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          position="right"
        />
        
        {/* Legacy context selectors — standalone header mode only (AppShell routes derive context). */}
        {showHeader && !sidebarCollapsed && !demoMode && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '8px',
            marginBottom: '8px'
          }}>
            <ChatUIModelSelector />
            <ChatUIRoleSelector />
          </div>
        )}
        
        {/* Pinned Artifacts - Hide in demo mode */}
        {!demoMode && (
          <SidebarSection 
            title="Pinned" 
            collapsed={sidebarCollapsed}
            showDivider={true}
          >
            <PinnedThreads />
          </SidebarSection>
        )}

        {/* Recent Threads - Hide in demo mode */}
        {!demoMode && (
          <SidebarSection 
            title="Recent Threads" 
            collapsed={sidebarCollapsed}
            showDivider={true}
          >
            <RecentThreads />
          </SidebarSection>
        )}
        
        {/* Demo Mode - Show collapsible scene-based navigation */}
        {demoMode && demoSession && (
          <SidebarSection 
            title="Interactive Demo" 
            collapsed={sidebarCollapsed}
            showDivider={true}
          >
            <div style={{ 
              color: 'white', 
              padding: '8px', 
              fontSize: '12px',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(59, 130, 246, 0.3)'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '11px' }}>
                {demoSession.session_key}
              </div>
              
              {/* Demo User Control */}
              <div style={{ 
                marginBottom: '12px', 
                padding: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{ 
                  fontSize: '10px', 
                  fontWeight: 'bold', 
                  marginBottom: '4px',
                  color: '#e5e7eb'
                }}>
                  Demo User
                </div>
                <select
                  style={{
                    width: '100%',
                    padding: '4px 6px',
                    fontSize: '10px',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px',
                    outline: 'none'
                  }}
                  value={demoSession.user_id || ''}
                  onChange={(e) => {
                    // TODO: Implement user switching
                    console.log('Demo user change requested:', e.target.value);
                  }}
                >
                  <option value="">Select Demo User</option>
                  <option value="demo-user-1">Demo User (Business Owner)</option>
                  <option value="demo-user-2">Alex Johnson (Operations Manager)</option>
                  <option value="demo-user-3">Sarah Chen (Financial Analyst)</option>
                </select>
              </div>
              
              {/* Demo Scenes with proper titles */}
              {[
                { id: 0, title: "Welcome to Krysalis", description: "Introduction and company setup", completed: demoSession.current_scene > 0 },
                { id: 1, title: "Creating the Living Model", description: "Business flow explanation and artifact creation", completed: demoSession.current_scene > 1 },
                { id: 2, title: "The Organization Layer", description: "Organizational assessment and scoring", completed: demoSession.current_scene > 2 },
                { id: 3, title: "The Business Value Layer", description: "Business value measurement and analysis", completed: demoSession.current_scene > 3 },
                { id: 4, title: "The Financial Flow", description: "Financial integration and forecasting", completed: demoSession.current_scene > 4 },
                { id: 5, title: "The Simulation Engine", description: "Scenario modeling and simulation", completed: demoSession.current_scene > 5 },
                { id: 6, title: "Continuous Learning and Self-Diagnosis", description: "Problem detection and correction", completed: demoSession.current_scene > 6 },
                { id: 7, title: "Integration and Intelligence", description: "System integration and data translation", completed: demoSession.current_scene > 7 },
                { id: 8, title: "Insight & Governance", description: "Reporting and governance features", completed: demoSession.current_scene > 8 },
                { id: 9, title: "Closing Vision", description: "Vision summary and lead capture", completed: demoSession.current_scene > 9 }
              ].map((scene, index) => {
                const isActive = scene.id === demoSession.current_scene;
                const isCompleted = scene.completed;
                const isNextAvailable = scene.id === demoSession.current_scene + 1;
                const isCollapsed = isCompleted; // Collapse completed scenes by default
                
                return (
                  <div key={scene.id} style={{ marginBottom: '6px' }}>
                    {/* Scene Header */}
                    <div 
                      style={{
                        backgroundColor: isActive ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                        border: isActive ? '1px solid #3b82f6' : '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '6px',
                        padding: '8px',
                        cursor: isActive ? 'default' : 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => {
                        if (isActive && demoSession.session_data && demoSession.session_data.thread_id) {
                          handleThreadClick(demoSession.session_data.thread_id);
                        }
                        // Always set the selected scene for help when clicked
                        setSelectedSceneForHelp(scene.id);
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {/* Checkbox/Checkmark */}
                          <div style={{ 
                            width: '12px', 
                            height: '12px', 
                            borderRadius: '2px',
                            border: '1px solid rgba(255, 255, 255, 0.4)',
                            backgroundColor: isCompleted ? '#10b981' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '8px',
                            color: 'white'
                          }}>
                            {isCompleted ? '✓' : ''}
                          </div>
                          
                          {/* Scene Info */}
                          <div 
                            style={{ 
                              fontWeight: 'bold', 
                              fontSize: '12px',
                              color: isActive ? '#60a5fa' : 'white',
                              cursor: 'help'
                            }}
                            title={scene.description}
                          >
                            {isActive ? '🎯 ' : ''}{scene.title}
                          </div>
                        </div>
                        
                        {/* Status/Action Button */}
                        {isActive && (
                          <div style={{ fontSize: '9px', color: '#60a5fa' }}>
                            Active
                          </div>
                        )}
                        {isCompleted && (
                          <div style={{ fontSize: '9px', color: '#10b981' }}>
                            ✓ Done
                          </div>
                        )}
                        {isNextAvailable && (
                          <button
                            style={{
                              fontSize: '8px',
                              padding: '2px 6px',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              // TODO: Implement scene advancement
                              console.log('Advance to scene', scene.id);
                            }}
                          >
                            Start
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Scene Thread (only show if active or expanded) */}
                    {isActive && demoSession.session_data && demoSession.session_data.thread_id && (
                      <div style={{ 
                        marginLeft: '20px', 
                        marginTop: '4px',
                        padding: '4px 8px',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        borderRadius: '4px',
                        border: '1px solid rgba(59, 130, 246, 0.2)'
                      }}>
                        <div 
                          style={{
                            fontSize: '10px',
                            color: '#60a5fa',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          onClick={() => handleThreadClick(demoSession.session_data.thread_id)}
                        >
                          <span>💬</span>
                          <span>{scene.title}</span>
                          {currentThreadId === demoSession.session_data.thread_id && (
                            <span style={{ fontSize: '8px', color: '#10b981' }}>✓</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* Progress Summary */}
              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '6px',
                padding: '6px',
                marginTop: '8px',
                fontSize: '10px',
                textAlign: 'center'
              }}>
                Progress: {demoSession.completed_scenes?.length || 0} of 10 scenes completed
              </div>
            </div>
          </SidebarSection>
        )}

        {/* Focus List - Hide in demo mode */}
        {!demoMode && (
          <SidebarSection 
            title="Focus List" 
            collapsed={sidebarCollapsed}
            showDivider={false}
          >
            <FocusList compact={true} showTitle={false} />
          </SidebarSection>
        )}
      </aside>

      {/* Center Chat */}
      <main
        className={styles.center}
        style={{
          backgroundColor: '#1e3a8a',
          padding: '16px',
          position: 'relative',
        }}
      >
        {/* Demo Help - only show in demo mode */}
        {demoMode && demoSession && (
          <div style={{
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '8px',
            marginBottom: '12px'
          }}>
            <DemoHelp
              currentScene={selectedSceneForHelp !== null ? selectedSceneForHelp : demoSession.current_scene}
              isExpanded={demoHelpExpanded}
              onToggleExpanded={() => setDemoHelpExpanded(!demoHelpExpanded)}
              sessionKey={demoSession.session_key}
            />
          </div>
        )}

        {/* Exo Panel - Next Action (UX Principle #3: "Next" Is Sacred) */}
        {/* Show in Execution Mode (operator) when not in demo mode */}
        {isExecutionMode && !demoMode && !hideNextActionPanel && (
          <div style={{ marginBottom: '16px' }}>
            <NextActionPanel
              showQueueIndicator={true}
              showRefresh={true}
              compact={false}
              onSignalResolved={(signal, resolutionType) => {
                console.log('Signal resolved:', signal.id, resolutionType);
                // Could add a message to chat when signal is resolved
                addMessage({
                  threadId: currentThreadId || 'default-thread',
                  who: 'assistant',
                  text: `✓ ${resolutionType === 'completed' ? 'Completed' : resolutionType === 'blocked' ? 'Blocked' : resolutionType}: ${signal.message}`,
                  type: 'text',
                  metadata: {} // Signal resolution logged to console
                });
              }}
            />
          </div>
        )}

        {/* History toggle for Execution Mode (UX Principle #10: Present-focused by default) */}
        {isExecutionMode && !demoMode && messages.filter(m => m.threadId === currentThreadId).length > 0 && (
          <button
            onClick={() => setShowHistory(!showHistory)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '6px',
              color: '#93c5fd',
              fontSize: '12px',
              cursor: 'pointer',
              marginBottom: '12px',
              width: 'fit-content'
            }}
          >
            {showHistory ? '▼ Hide History' : '▶ Show History'} ({messages.filter(m => m.threadId === currentThreadId).length} messages)
          </button>
        )}

        <div style={{ 
          flex: 1, 
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          // UX Principle #10: Hide history by default in Execution Mode
          ...(isExecutionMode && !demoMode && !showHistory ? { display: 'none' } : {})
        }} ref={centerRef}>
          {messages
            .filter(message => message.threadId === currentThreadId)
            .map((message) => {
            // Debug: Log message structure for demo mode
            if (demoMode && message.metadata) {
              console.log('🔍 [Message Debug]', {
                messageId: message.id,
                type: message.type,
                hasMetadata: !!message.metadata,
                cardType: message.metadata?.cardType,
                text: message.text?.substring(0, 50)
              });
            }
            
            return (
            <div key={message.id} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Message Text */}
              {message.text && (
                <div style={{
                  display: 'flex',
                  justifyContent: message.who === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: '8px',
                }}>
                  <div style={{
                    maxWidth: '70%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    backgroundColor: message.who === 'user' ? '#3b82f6' : '#60a5fa',
                    color: 'white',
                    fontSize: '14px',
                    lineHeight: '1.4',
                    wordWrap: 'break-word',
                    position: 'relative',
                  }}>
                    {/* Command indicator for assistant messages */}
                    {message.who === 'assistant' && chatVisibleModelRead(message.metadata) ? (
                      <div data-testid="chat-model-read">{chatVisibleModelRead(message.metadata)}</div>
                    ) : null}
                    {message.who === 'assistant' && message.metadata?.commandHistory && (
                      <div style={{
                        position: 'absolute',
                        top: '-8px',
                        left: '8px',
                        backgroundColor: '#1f2937',
                        color: '#fbbf24',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 'bold',
                      }}>
                        /{message.metadata.commandHistory.command}
                        {message.metadata.commandHistory.args.length > 0 && 
                          ` ${message.metadata.commandHistory.args.join(' ')}`}
                      </div>
                    )}
                    <ReactMarkdown
                      components={{
                        // Customize markdown components for better styling in chat
                        h1: ({ children }) => <h1 style={{ color: 'white', fontSize: '18px', margin: '0 0 8px 0' }}>{children}</h1>,
                        h2: ({ children }) => <h2 style={{ color: 'white', fontSize: '16px', margin: '0 0 6px 0' }}>{children}</h2>,
                        h3: ({ children }) => <h3 style={{ color: 'white', fontSize: '14px', margin: '0 0 4px 0' }}>{children}</h3>,
                        p: ({ children }) => <p style={{ color: 'white', margin: '0 0 8px 0' }}>{children}</p>,
                        strong: ({ children }) => <strong style={{ color: 'white', fontWeight: 'bold' }}>{children}</strong>,
                        code: ({ children, className }) => {
                          const isInline = !className;
                          const codeString = String(children).replace(/\n$/, '');
                          
                          // Check if it's JSON and try to format it
                          let formattedCode = codeString;
                          if (!isInline && (codeString.trim().startsWith('{') || codeString.trim().startsWith('['))) {
                            try {
                              const parsed = JSON.parse(codeString);
                              formattedCode = JSON.stringify(parsed, null, 2);
                            } catch (e) {
                              // Not valid JSON, use as-is
                            }
                          }
                          
                          if (isInline) {
                            return (
                              <code style={{
                                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                padding: '2px 4px',
                                borderRadius: '3px',
                                fontSize: '12px',
                                color: '#fbbf24'
                              }}>
                                {formattedCode}
                              </code>
                            );
                          }
                          
                          return (
                            <pre style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.3)',
                              padding: '12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              color: '#fbbf24',
                              overflow: 'auto',
                              margin: '8px 0',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}>
                              <code>{formattedCode}</code>
                            </pre>
                          );
                        },
                        table: ({ children }) => (
                          <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            margin: '8px 0',
                            fontSize: '12px'
                          }}>
                            {children}
                          </table>
                        ),
                        th: ({ children }) => (
                          <th style={{
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            padding: '6px 8px',
                            backgroundColor: 'rgba(0, 0, 0, 0.2)',
                            color: 'white',
                            fontWeight: 'bold',
                            textAlign: 'left'
                          }}>
                            {children}
                          </th>
                        ),
                        td: ({ children }) => (
                          <td style={{
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            padding: '6px 8px',
                            color: 'white'
                          }}>
                            {children}
                          </td>
                        ),
                        hr: () => <hr style={{ border: '1px solid rgba(255, 255, 255, 0.2)', margin: '12px 0' }} />,
                        ul: ({ children }) => <ul style={{ color: 'white', margin: '0 0 8px 0', paddingLeft: '20px' }}>{children}</ul>,
                        ol: ({ children }) => <ol style={{ color: 'white', margin: '0 0 8px 0', paddingLeft: '20px' }}>{children}</ol>,
                        li: ({ children }) => <li style={{ color: 'white', margin: '2px 0' }}>{children}</li>,
                      }}
                    >
                      {message.text}
                    </ReactMarkdown>
                  </div>
                </div>
              )}

              {/* Command Suggestions - Display buttons for suggested commands */}
              {message.who === 'assistant' && message.metadata?.command_suggestions && message.metadata.command_suggestions.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  marginTop: '8px',
                  marginBottom: '8px',
                }}>
                  <Text type="secondary" style={{ fontSize: '12px', marginBottom: '4px' }}>
                    Suggested Commands:
                  </Text>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                  }}>
                    {message.metadata.command_suggestions.map((cmd: any, idx: number) => (
                      <button
                        key={idx}
                        onClick={async () => {
                          if (!user || !currentThreadId) return;
                          try {
                            const commandText = `/${cmd.command}${cmd.args && cmd.args.length > 0 ? ' ' + cmd.args.join(' ') : ''}`;
                            await executeCommand(commandText);
                            // Optionally send the command as a message
                            await sendMessage(commandText, 'command');
                          } catch (error) {
                            console.error('Failed to execute command:', error);
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#059669';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#10b981';
                        }}
                      >
                        {cmd.full_text || `/${cmd.command}${cmd.args && cmd.args.length > 0 ? ' ' + cmd.args.join(' ') : ''}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Message Chips */}
              {message.chips && (
                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  flexWrap: 'wrap',
                  justifyContent: message.who === 'user' ? 'flex-end' : 'flex-start',
                }}>
                  {Array.isArray(message.chips) ? message.chips : []}
                </div>
              )}

              {/* Card Messages */}
              {message.type === 'card' && message.metadata && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: message.who === 'user' ? 'flex-end' : 'flex-start',
                  marginTop: '8px',
                  position: 'relative',
                }}>
                  {/* Model read and command indicator for card messages */}
                  {chatVisibleModelRead(message.metadata) ? (
                    <div data-testid="chat-model-read">{chatVisibleModelRead(message.metadata)}</div>
                  ) : null}
                  {message.metadata?.commandHistory && (
                    <div style={{
                      backgroundColor: '#1f2937',
                      color: '#fbbf24',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      alignSelf: 'flex-start',
                      marginBottom: '4px',
                    }}>
                      /{message.metadata.commandHistory.command}
                      {message.metadata.commandHistory.args.length > 0 && 
                        ` ${message.metadata.commandHistory.args.join(' ')}`}
                    </div>
                  )}
                  
                  {/* Use Composite Card Renderer */}
                  <CompositeCardRenderer
                    message={message}
                    context={{
                      threadId: currentThreadId || (demoMode ? 'demo-thread' : 'default-thread'),
                      userId: user?.id || 'current-user',
                      uiRole: layoutRole,
                      modelType: env,
                      dataSource: dataSource
                    }}
                    onCardSelect={(card) => {
                      console.log('Card selected:', card);
                      // Handle card selection
                    }}
                    onCardAction={handleLocalCardAction}
                  />
                </div>
              )}

              {/* Artifact List Messages */}
              {(message.type as any) === 'artifact_list' && (message as any).cards && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: message.who === 'user' ? 'flex-end' : 'flex-start',
                  marginTop: '8px',
                  position: 'relative',
                }}>
                  {/* Model read and command indicator for artifact list messages */}
                  {chatVisibleModelRead(message.metadata) ? (
                    <div data-testid="chat-model-read">{chatVisibleModelRead(message.metadata)}</div>
                  ) : null}
                  {message.metadata?.commandHistory && (
                    <div style={{
                      backgroundColor: '#1f2937',
                      color: '#fbbf24',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      alignSelf: 'flex-start',
                      marginBottom: '4px',
                    }}>
                      /{message.metadata.commandHistory.command}
                      {message.metadata.commandHistory.args.length > 0 && 
                        ` ${message.metadata.commandHistory.args.join(' ')}`}
                    </div>
                  )}
                  <CompositeCardRenderer
                    message={{
                      ...message,
                      metadata: {
                        ...message.metadata,
                        cardType: 'artifact-list',
                        items: (message as any).cards,
                        itemType: 'artifact'
                      }
                    }}
                    context={{
                      uiRole: layoutRole,
                      modelType: env || 'Prime',
                      threadId: currentThreadId || (demoMode ? 'demo-thread' : 'default-thread'),
                      userId: user?.id || 'current-user',
                      dataSource: dataSource
                    }}
                    onCardSelect={(card) => {
                      console.log('🎯 [ChatOperationalLayout] Card selected:', card);
                      // Handle card selection if needed
                    }}
                    onCardAction={(action, payload) => {
                      handleLocalCardAction(action, payload);
                      if (action === 'select_artifact') {
                        // Create artifact card using the composite card system
                        addMessage({
                          threadId: currentThreadId || 'default-thread',
                          who: 'assistant',
                          text: `Artifact details for "${payload.artifact?.name || 'Unknown'}":`,
                          type: 'card',
                          metadata: {
                            cardType: 'artifact-detail',
                            artifactId: payload.artifactId,
                            artifact: payload.artifact,
                            commandHistory: {
                              command: 'select_artifact',
                              args: [payload.artifactId],
                              executionTime: Date.now(),
                              dataSource: dataSource,
                              success: true
                            }
                          } as any
                        });
                      }
                    }}
                  />
                </div>
              )}

              {/* Thread List Messages */}
              {(message.type as any) === 'thread_list' && (message as any).cards && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: message.who === 'user' ? 'flex-end' : 'flex-start',
                  marginTop: '8px',
                  position: 'relative',
                }}>
                  {/* Model read and command indicator for thread list messages */}
                  {chatVisibleModelRead(message.metadata) ? (
                    <div data-testid="chat-model-read">{chatVisibleModelRead(message.metadata)}</div>
                  ) : null}
                  {message.metadata?.commandHistory && (
                    <div style={{
                      backgroundColor: '#1f2937',
                      color: '#fbbf24',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      alignSelf: 'flex-start',
                      marginBottom: '4px',
                    }}>
                      /{message.metadata.commandHistory.command}
                      {message.metadata.commandHistory.args.length > 0 && 
                        ` ${message.metadata.commandHistory.args.join(' ')}`}
                    </div>
                  )}
                  {/* TODO: Create ThreadListCard component similar to ArtifactListCard */}
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{message.text}</h3>
                    <div className="text-sm text-gray-600">
                      Found {(message as any).cards?.length || 0} threads
                    </div>
                  </div>
                </div>
              )}

              {/* Timestamp */}
              <div style={{
                fontSize: '10px',
                color: '#93c5fd',
                textAlign: message.who === 'user' ? 'right' : 'left',
                marginTop: '4px',
              }}>
                {new Date(message.timestamp).toLocaleTimeString()}
              </div>
            </div>
            );
          })}
          
          {/* Live Data Welcome Message */}
          {dataSource === 'live' && messages.length === 0 && !isLoadingLiveData && !liveDataError && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              flexDirection: 'column',
              gap: '16px',
              padding: '32px'
            }}>
              <div style={{
                textAlign: 'center',
                maxWidth: '500px'
              }}>
                <h3 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px' 
                }}>
                  Welcome to Live Data Mode
                </h3>
                <p style={{ 
                  fontSize: '16px', 
                  color: '#93c5fd', 
                  lineHeight: '1.6',
                  marginBottom: '24px'
                }}>
                  You're now connected to live data from your {env} environment. 
                  You can interact with real artifacts, processes, and data.
                </p>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  alignItems: 'center'
                }}>
                  <p style={{ fontSize: '14px', color: 'white', fontWeight: '500' }}>
                    Try these commands to get started:
                  </p>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap',
                    justifyContent: 'center'
                  }}>
                    {['/help', '/items', '/map', '/process'].map(cmd => (
                      <button
                        key={cmd}
                        onClick={() => void handleCommand(cmd)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#3b82f6',
                          border: '1px solid #2563eb',
                          borderRadius: '6px',
                          fontSize: '14px',
                          color: 'white',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                      >
                        {cmd}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Live Data Error Message */}
          {dataSource === 'live' && liveDataError && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              flexDirection: 'column',
              gap: '16px',
              padding: '32px'
            }}>
              <div style={{
                textAlign: 'center',
                maxWidth: '500px',
                padding: '24px',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px'
              }}>
                <h3 style={{ 
                  fontSize: '20px', 
                  fontWeight: '600', 
                  color: '#dc2626', 
                  marginBottom: '12px' 
                }}>
                  Live Data Connection Issue
                </h3>
                <p style={{ 
                  fontSize: '14px', 
                  color: '#7f1d1d', 
                  lineHeight: '1.6',
                  marginBottom: '16px'
                }}>
                  {liveDataError}
                </p>
                <p style={{ 
                  fontSize: '14px', 
                  color: '#7f1d1d', 
                  lineHeight: '1.6'
                }}>
                  The system has fallen back to sample data. You can still use the Exo Panel, 
                  but you'll be working with demo data instead of live data.
                </p>
              </div>
            </div>
          )}


          {/* Demo cards for initial display - only show when no thread is selected and no messages */}
          {!demoMode && messages.length === 0 && artifacts.length > 0 && !currentThreadId && (
            <>
              {/* Display all available artifacts using Composite Card System */}
              {artifacts.map((artifact) => (
                <div
                  key={artifact.id}
                  onClick={() => selectCenterCard(artifact.id, 'artifact')}
                  style={{ cursor: 'pointer' }}
                >
                  <CompositeCardRenderer
                    message={{
                      id: artifact.id,
                      threadId: currentThreadId || 'default-thread',
                      who: 'assistant',
                      text: `Artifact: ${artifact.name}`,
                      timestamp: new Date().toISOString(),
                      type: 'card',
                      metadata: {
                        cardType: 'artifact',
                        artifactId: artifact.id,
                        artifact: artifact
                      }
                    }}
                    context={{
                      uiRole: layoutRole,
                      modelType: env || 'Company',
                      threadId: currentThreadId || (demoMode ? 'demo-thread' : 'default-thread'),
                      userId: user?.id || 'current-user',
                      dataSource: dataSource
                    }}
                    onCardSelect={(card) => {
                      console.log('🎯 [ChatOperationalLayout] Initial artifact card selected:', card);
                      selectCenterCard(artifact.id, 'artifact');
                    }}
                    onCardAction={handleLocalCardAction}
                  />
                </div>
              ))}
            </>
          )}
          
          {/* Show loading state when messages are actively being loaded */}
          {currentThreadId && isLoadingMessages && messages.length === 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '14px'
            }}>
              <LoadingOutlined style={{ marginRight: '8px' }} />
              Loading thread content...
            </div>
          )}
        </div>

        {/* Thread Context - Show demo thread info when in demo mode */}
        {demoMode && demoSession ? (
          <div style={{
            padding: '12px 16px',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '8px',
            marginBottom: '12px'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              color: '#60a5fa'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  Demo Thread: {demoSession.session_data?.thread_name || 'Welcome to Krysalis'}
                </span>
                <span style={{ fontSize: '12px', opacity: 0.8 }}>
                  Scene {demoSession.current_scene + 1}
                </span>
              </div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>
                Session: {demoSession.session_key}
              </div>
            </div>
          </div>
        ) : (
          <ThreadContext />
        )}
        
        {/* Chat Input */}
        <div style={{ 
          padding: '16px 0 0 0',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <AgentReplyWaitIndicator
            active={isTyping}
            style={{ color: 'rgba(255,255,255,0.65)', marginBottom: 8, marginTop: 0 }}
          />
          <ChatInput
            onSendMessage={handleSendMessage}
            onCommand={(command, args) => void handleCommand(command, args)}
            placeholder="Type a message or use / for commands..."
            disabled={chatLoading || isTyping}
          />
        </div>
      </main>

      {/* Right Context Panel - UX Principle #6: Context is pull-based */}
      <aside
        className={styles.rightPanel}
        style={{
          width: '300px',
          backgroundColor: '#1e3a8a',
          padding: '16px',
          // In Execution Mode, context is minimized by default (pull-based)
          ...(isExecutionMode && !showContextPanel && !selectedArtifactForContext
            ? {
                display: 'flex',
                flexDirection: 'column' as const,
                alignItems: 'center',
                justifyContent: 'flex-start',
              }
            : {}),
        }}
      >
        {/* Context toggle for Execution Mode (UX Principle #6) */}
        {isExecutionMode && !demoMode && (
          <button
            onClick={() => setShowContextPanel(!showContextPanel)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              backgroundColor: showContextPanel ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '6px',
              color: '#93c5fd',
              fontSize: '12px',
              cursor: 'pointer',
              marginBottom: '16px',
              width: '100%',
              justifyContent: 'center'
            }}
          >
            {showContextPanel ? '✕ Hide Context' : '? Show Context'}
          </button>
        )}

        {/* Minimized state message for Execution Mode */}
        {isExecutionMode && !showContextPanel && !selectedArtifactForContext && !demoMode && (
          <div style={{
            textAlign: 'center',
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '12px',
            padding: '20px',
            lineHeight: 1.5
          }}>
            <div style={{ marginBottom: '8px', fontSize: '24px' }}>💡</div>
            Context is available on demand.
            <br />
            Click "Show Context" for details.
          </div>
        )}

        {/* Artifact Context Card (takes priority if set) */}
        {(showContextPanel || selectedArtifactForContext || !isExecutionMode || demoMode) && (
          selectedArtifactForContext ? (
            <ArtifactContextCard 
              artifact={selectedArtifactForContext}
              threadArtifactTreeId={currentThread?.model?.artifactTreeId}
              threadCompanyId={currentThread?.context?.companyId}
              onSectionToggle={(section, isExpanded) => {
                console.log(`Context section ${section} ${isExpanded ? 'expanded' : 'collapsed'}`);
              }}
            />
          ) : rightPanelContent ? (
          <div>
            <Text strong style={{ fontSize: '16px', marginBottom: '16px', display: 'block', color: 'white' }}>
              {rightPanelContent === 'artifact-details' && 'Artifact Details'}
              {rightPanelContent === 'thread-details' && 'Thread Details'}
              {rightPanelContent === 'task-details' && 'Task Details'}
            </Text>
            
            {rightPanelContent === 'artifact-details' && selectedPinnedArtifactId && (() => {
              // For pinned artifacts, the ID might be prefixed with "pinned-"
              let selectedArtifact = artifacts.find(a => a.id === selectedPinnedArtifactId);
              
              // If not found directly, try to find by removing the "pinned-" prefix
              if (!selectedArtifact && selectedPinnedArtifactId.startsWith('pinned-')) {
                const actualArtifactId = selectedPinnedArtifactId.replace('pinned-', '');
                selectedArtifact = artifacts.find(a => a.id === actualArtifactId);
              }
              
              return selectedArtifact ? (
                <CompositeCardRenderer
                  message={{
                    id: selectedArtifact.id,
                    threadId: currentThreadId || 'default-thread',
                    who: 'assistant',
                    text: `Artifact: ${selectedArtifact.name}`,
                    timestamp: new Date().toISOString(),
                    type: 'card',
                    metadata: {
                      cardType: 'artifact',
                      artifactId: selectedArtifact.id,
                      artifact: selectedArtifact
                    }
                  }}
                  context={{
                    uiRole: layoutRole,
                    modelType: env || 'Company',
                    threadId: currentThreadId || (demoMode ? 'demo-thread' : 'default-thread'),
                    userId: user?.id || 'current-user',
                    dataSource: dataSource
                  }}
                  onCardSelect={(card) => {
                    console.log('🎯 [ChatOperationalLayout] Pinned artifact card selected:', card);
                    selectCenterCard(selectedArtifact.id, 'artifact');
                  }}
                  onCardAction={handleLocalCardAction}
                />
              ) : (
                <Text style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Artifact not found: {selectedPinnedArtifactId}
                </Text>
              );
            })()}
            
            {rightPanelContent === 'task-details' && selectedFocusTaskId && (() => {
              // Create different mock tasks based on the selected focus task ID
              const getMockTask = (taskId: string) => {
                switch (taskId) {
                  case 'task-1':
                    return {
                      id: taskId,
                      title: 'Review Financial Reconciliation Process',
                      description: 'Monthly reconciliation process needs review and optimization. Current process takes too long.',
                      type: 'process' as const,
                      priority: 'high' as const,
                      status: 'in_progress' as const,
                      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                      assigneeId: 'u1',
                      assigneeName: 'Finance Manager',
                      artifactId: 'a4',
                      artifactName: 'Treasury: Reconcile Accounts',
                      progress: 65,
                      tags: ['finance', 'reconciliation', 'optimization']
                    };
                  case 'task-2':
                    return {
                      id: taskId,
                      title: 'Optimize Customer Onboarding Workflow',
                      description: 'Streamline the customer onboarding process to reduce time-to-value and improve satisfaction scores.',
                      type: 'improvement' as const,
                      priority: 'critical' as const,
                      status: 'pending' as const,
                      dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
                      assigneeId: 'u2',
                      assigneeName: 'Customer Success Manager',
                      artifactId: 'a1',
                      artifactName: 'Customer Onboarding Process',
                      progress: 25,
                      tags: ['customer', 'onboarding', 'optimization', 'workflow']
                    };
                  case 'task-3':
                    return {
                      id: taskId,
                      title: 'Implement Automated Reporting System',
                      description: 'Develop and deploy automated reporting system for monthly financial statements and KPIs.',
                      type: 'artifact' as const,
                      priority: 'medium' as const,
                      status: 'in_progress' as const,
                      dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
                      assigneeId: 'u3',
                      assigneeName: 'IT Developer',
                      artifactId: 'a2',
                      artifactName: 'Financial Reporting System',
                      progress: 80,
                      tags: ['automation', 'reporting', 'finance', 'development']
                    };
                  default:
                    return {
                      id: taskId,
                      title: 'General Task',
                      description: 'A general focus task for demonstration purposes.',
                      type: 'review' as const,
                      priority: 'low' as const,
                      status: 'pending' as const,
                      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                      assigneeId: 'u4',
                      assigneeName: 'Project Manager',
                      progress: 10,
                      tags: ['general', 'review']
                    };
                }
              };
              
              const mockTask = getMockTask(selectedFocusTaskId);
              
              return (
                <TaskCard 
                  task={mockTask}
                  onSelect={(taskId) => {
                    console.log('Task selected:', taskId);
                    // Could navigate to task details or start a discussion
                  }}
                />
              );
            })()}
            
            {rightPanelContent === 'thread-details' && selectedThreadId && (
              <div>
                <Text style={{ color: 'white' }}>Thread: {selectedThreadId}</Text>
                <Text style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}>
                  Thread details will be displayed here
                </Text>
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '32px 16px' }}>
            <Text style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Select an item to view details
            </Text>
          </div>
        ))}
      </aside>

      {/* Command Help Modal */}
      {showCommandHelp && (
        <CommandHelp
          visible={showCommandHelp}
          onClose={() => setShowCommandHelp(false)}
          selectedCommand={selectedCommand}
        />
      )}

      {/* Thread Assignment Dialog */}
      <ThreadAssignmentDialog
        visible={threadAssignmentDialog.visible}
        threadData={threadAssignmentDialog.threadData}
        currentUser={user}
        onConfirm={handleThreadAssignment}
        onCancel={handleThreadAssignmentCancel}
      />
    </div>
  );
};

export default ChatOperationalLayout;
